/**
 * fitToLongestLine
 *
 * Sets the max-width of a block element so that it is exactly as wide as its
 * longest rendered text line (plus padding), eliminating the trailing whitespace
 * that browsers leave when text wraps.
 *
 * Works with:
 * - Single text nodes (p, h1–h6, …)
 * - Mixed content (h2 + p + a inside a div)
 * - box-sizing: content-box and border-box
 * - text-align: left, center, right
 * - text-wrap: balance and wrap
 * - Explicit <br> line breaks
 * - Single-line and no-wrap text
 *
 * Performance notes:
 * - Measures on an invisible in-flow clone to avoid flicker on the original
 * - The original element is written to at most once per call (early exit if unchanged)
 * - Uses a single shared ResizeObserver for all observed elements
 * - Debounces via requestAnimationFrame to batch simultaneous resizes
 * - Fonts are awaited via document.fonts.ready before the initial measurement
 */

const CLONE_STYLES: Partial<CSSStyleDeclaration> = {
  // Hide visually without removing from flow – clone stays in the same
  // containing block as the original and inherits the same available width
  clipPath: "rect(0 0 0 0)",
  height: "0",
  paddingBlock: "0",
  marginBlock: "0",
  overflow: "hidden",
  pointerEvents: "none",
  // Remove any previously applied constraints so we measure the natural layout
  width: "",
  maxWidth: "none",
};

function applyCloneStyles(el: HTMLElement): void {
  Object.assign(el.style, CLONE_STYLES);
  el.setAttribute("aria-hidden", "true");
  el.setAttribute("tabindex", "-1");
}

function collectLineWidths(container: HTMLElement, widths: number[]): void {
  const range = document.createRange();
  // Track per visual line: the leftmost and rightmost pixel across all text
  // segments on that line (works across element boundaries like <a> + text).
  const lines = new Map<number, { left: number; right: number }>();

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node: Text;
  while ((node = walker.nextNode() as Text)) {
    const text = node.textContent ?? "";
    if (!text.trim()) continue;
    // Measure the whole text node at once – getClientRects returns one rect
    // per wrapped line segment, split at element boundaries but not mid-word.
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) {
      if (rect.width <= 0) continue;
      const key = Math.round(rect.top);
      const line = lines.get(key);
      if (!line) {
        lines.set(key, { left: rect.left, right: rect.right });
      } else {
        line.left = Math.min(line.left, rect.left);
        line.right = Math.max(line.right, rect.right);
      }
    }
  }

  for (const { left, right } of lines.values()) {
    widths.push(right - left);
  }
}

export function fitToLongestLine(el: HTMLElement): void {
  const s = window.getComputedStyle(el);
  const hPadding = parseFloat(s.paddingLeft) + parseFloat(s.paddingRight);
  const isBorderBox = s.boxSizing === "border-box";

  // Create an in-flow clone directly after the original.
  // Being in the same parent means it inherits the same containing block
  // and available width – the text wraps identically to the original.
  const clone = el.cloneNode(true) as HTMLElement;
  applyCloneStyles(clone);
  el.insertAdjacentElement("afterend", clone);

  const lineWidths: number[] = [];
  collectLineWidths(clone, lineWidths);

  clone.remove();

  if (lineWidths.length === 0) return;

  const longestLine = Math.max(...lineWidths);
  const SUBPIXEL_BUFFER = 1; // Accounts for browser subpixel rounding
  const newMaxWidth = Math.ceil(longestLine + (isBorderBox ? hPadding : 0)) + SUBPIXEL_BUFFER;

  // Early exit – only write to the original if the value actually changed,
  // avoiding an unnecessary reflow and preventing ResizeObserver feedback loops
  if (el.style.maxWidth === `${newMaxWidth}px`) return;

  el.style.width = "fit-content";
  el.style.maxWidth = `${newMaxWidth}px`;

}

/**
 * Observes one or more elements and re-runs fitToLongestLine whenever their
 * parent container resizes or new fonts load.
 *
 * Accepts a CSS selector string, a single HTMLElement, or an array of elements.
 * Returns a cleanup function (for use in useEffect, vue onUnmounted, etc.).
 *
 * @example
 * const cleanup = await observeFit('[data-fit-to-longest-line]');
 * const cleanup = await observeFit(document.querySelector('p.headline'));
 * const cleanup = await observeFit([el1, el2]);
 */
export async function observeFit(
  target: string | HTMLElement | HTMLElement[]
): Promise<() => void> {
  const els: HTMLElement[] =
    typeof target === "string"
      ? Array.from(document.querySelectorAll<HTMLElement>(target))
      : Array.isArray(target)
      ? target
      : [target];

  if (els.length === 0) return () => {};

  let rafId: number;

  const ro = new ResizeObserver((entries) => {
    const changedParents = new Set(entries.map((e) => e.target));
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      els.forEach((el) => {
        if (changedParents.has(el.parentElement ?? el)) {
          fitToLongestLine(el);
        }
      });
    });
  });

  await document.fonts.ready;

  els.forEach((el) => {
    fitToLongestLine(el);
    // Observe the parent so our own width changes don't re-trigger the observer
    ro.observe(el.parentElement ?? el);
  });

  function onFontsLoaded(): void {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => els.forEach(fitToLongestLine));
  }

  document.fonts.addEventListener("loadingdone", onFontsLoaded);

  return () => {
    ro.disconnect();
    document.fonts.removeEventListener("loadingdone", onFontsLoaded);
    cancelAnimationFrame(rafId);
  };
}